const KEY = 'eizel_auth_error'

export interface StoredAuthError {
  error: string
  description?: string
}

// Auth0 returns post-login Action denials (e.g. the email-verification gate)
// to `redirect_uri` as `?error=access_denied&error_description=...`. Because
// redirect_uri is the bare origin, that lands on `/`, which ProtectedRoute
// immediately bounces to `/login`. The Auth0 SDK's in-memory `error` does not
// reliably survive that async-init + route bounce, so the denial reason was
// being lost and users saw a blank login page. We snapshot the error straight
// off the URL at app entry — before the router or SDK can strip it — so
// LoginPage can always explain why sign-in was refused.
export function captureAuthErrorFromUrl(search: string = window.location.search): void {
  const params = new URLSearchParams(search)
  const error = params.get('error')
  if (!error) return
  const stored: StoredAuthError = {
    error,
    description: params.get('error_description') ?? undefined,
  }
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(stored))
  } catch {
    // sessionStorage unavailable (private mode / disabled) — the in-memory
    // Auth0 error is still the primary path; we just lose the bounce fallback.
  }
}

export function getStoredAuthError(): StoredAuthError | null {
  try {
    const raw = window.sessionStorage.getItem(KEY)
    return raw ? (JSON.parse(raw) as StoredAuthError) : null
  } catch {
    return null
  }
}

export function clearStoredAuthError(): void {
  try {
    window.sessionStorage.removeItem(KEY)
  } catch {
    // ignore
  }
}
